// src/views/AccommodationProvider/ProviderAddPropertyContact.js
import React, { useState } from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderAddPropertyContact = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    id_type: '',
    id_number: '',
    phone: '',
    email: '',
    property_phone: '',
    property_email: '',
    fax: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});

  // Title options
  const titleOptions = [
    'Mr', 'Ms', 'Mrs', 'Dr', 'Prof', 'Rev'
  ];

  // ID Type options
  const idTypeOptions = [
    'South African ID',
    'Passport',
    "Driver's License",
    'Work Permit',
    'Student Permit',
    'Asylum Seeker Permit'
  ];

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Property Contact Details - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Property Contact Details - ${providerName || 'Your Organization'}`;
    } else {
      return 'Property Contact Details';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBackToRoomDetails = () => {
    navigate('/provider-add-property-room-details');
  };

  const handleBackToPropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });
    setValidationErrors({});

    // Basic client-side validation
    const errors = {};
    
    // Validate first name
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required.';
    }

    // Validate last name
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required.';
    }

    // Validate title
    if (!formData.title) {
      errors.title = 'Title is required.';
    }

    // Validate ID type
    if (!formData.id_type) {
      errors.id_type = 'ID type is required.';
    }

    // Validate ID number
    if (!formData.id_number.trim()) {
      errors.id_number = 'ID number is required.';
    }

    // Validate phone number
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required.';
    } else if (formData.phone.trim().length < 10) {
      errors.phone = 'Phone number must be at least 10 characters.';
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address.';
    }

    // Validate property phone
    if (!formData.property_phone.trim()) {
      errors.property_phone = 'Property phone number is required.';
    } else if (formData.property_phone.trim().length < 10) {
      errors.property_phone = 'Property phone number must be at least 10 characters.';
    }

    // Validate property email
    if (!formData.property_email.trim()) {
      errors.property_email = 'Property email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.property_email.trim())) {
      errors.property_email = 'Please enter a valid email address.';
    }

    // Fax is optional - no validation needed

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setAlert({
        type: 'danger',
        message: 'Please fill in all required fields correctly.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const requestData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        title: formData.title || null,
        id_type: formData.id_type || null,
        id_number: formData.id_number.trim() || null,
        phone: formData.phone.trim(),
        email: formData.email.trim().toLowerCase(),
        property_phone: formData.property_phone.trim(),
        property_email: formData.property_email.trim().toLowerCase(),
        fax: formData.fax.trim() || null
      };
      
      console.log('Sending complete property creation request data:', requestData);
      
      const response = await axiosInstance.post(
        `/api/property/contact/store/`,
        requestData
      );

      if (response.status === 200) {
        setAlert({
          type: 'success',
          message: response.data.message || 'Contact details saved successfully! Proceeding to campus selection.',
          visible: true
        });
        
        // Navigate to campus selection after a delay
        setTimeout(() => {
          navigate('/provider-add-property-campuses');
        }, 1500);
      }
    } catch (error) {
      console.error('Error creating complete property:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data?.validation_errors) {
        const backendValidationErrors = error.response.data.validation_errors;
        
        // Convert backend validation errors to frontend format
        const frontendErrors = {};
        Object.entries(backendValidationErrors).forEach(([field, messages]) => {
          if (field === 'contact' && typeof messages === 'object') {
            // Handle nested contact validation errors
            Object.entries(messages).forEach(([contactField, contactMessages]) => {
              frontendErrors[contactField] = Array.isArray(contactMessages) ? contactMessages[0] : contactMessages;
            });
          } else {
            frontendErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          }
        });
        
        setValidationErrors(frontendErrors);
        setAlert({
          type: 'danger',
          message: 'Please correct the errors.',
          visible: true
        });
      } else if (error.response?.data?.error) {
        setAlert({
          type: 'danger',
          message: error.response.data.error,
          visible: true
        });
      } else {
        setAlert({
          type: 'danger',
          message: `Failed to create property. Status: ${error.response?.status}. Please try again.`,
          visible: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary" className="me-2">
                Step 3 of 5
              </Badge>
            </div>
            <div>
              <button 
                type="button"
                className="btn btn-outline-secondary btn-sm me-2"
                onClick={handleBackToRoomDetails}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Room Details
              </button>
              <button 
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleBackToPropertyManagement}
              >
                <i className="bi bi-house me-2"></i>
                Property Management
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Property Contact Form */}
      <Row className="mb-4">
        <Col md="10" className="mx-auto">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-person-lines-fill me-2 text-primary"></i>
                Property Contact Information
              </CardTitle>
              
              <form onSubmit={handleSubmit}>
                <h3>Primary Contact Person</h3>

                {/* Title and First Name - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="form-floating">
                      <select
                        className={`form-select ${validationErrors.title ? 'is-invalid' : ''}`}
                        id="titleInput"
                        value={formData.title}
                        onChange={handleInputChange}
                        name="title"
                        disabled={regStatus !== '2' || isSubmitting}
                      >
                        <option value="">Select Title</option>
                        {titleOptions.map((title) => (
                          <option key={title} value={title}>
                            {title}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="titleInput">Title *</label>
                      {validationErrors.title && (
                        <div className="invalid-feedback">{validationErrors.title}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.first_name ? 'is-invalid' : ''}`}
                        type="text"
                        id="firstNameInput"
                        name="first_name"
                        placeholder="First Name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="firstNameInput">First Name *</label>
                      {validationErrors.first_name && (
                        <div className="invalid-feedback">{validationErrors.first_name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.last_name ? 'is-invalid' : ''}`}
                        type="text"
                        id="lastNameInput"
                        name="last_name"
                        placeholder="Last Name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="lastNameInput">Last Name *</label>
                      {validationErrors.last_name && (
                        <div className="invalid-feedback">{validationErrors.last_name}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ID Type and ID Number - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <select
                        className={`form-select ${validationErrors.id_type ? 'is-invalid' : ''}`}
                        id="idTypeInput"
                        value={formData.id_type}
                        onChange={handleInputChange}
                        name="id_type"
                        disabled={regStatus !== '2' || isSubmitting}
                      >
                        <option value="">Select ID Type</option>
                        {idTypeOptions.map((idType) => (
                          <option key={idType} value={idType}>
                            {idType}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="idTypeInput">ID Type *</label>
                      {validationErrors.id_type && (
                        <div className="invalid-feedback">{validationErrors.id_type}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.id_number ? 'is-invalid' : ''}`}
                        type="text"
                        id="idNumberInput"
                        name="id_number"
                        placeholder="ID Number"
                        value={formData.id_number}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="idNumberInput">ID Number *</label>
                      {validationErrors.id_number && (
                        <div className="invalid-feedback">{validationErrors.id_number}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phone and Email - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.phone ? 'is-invalid' : ''}`}
                        type="tel"
                        id="phoneInput"
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="phoneInput">Phone Number *</label>
                      {validationErrors.phone && (
                        <div className="invalid-feedback">{validationErrors.phone}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                        type="email"
                        id="emailInput"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="emailInput">Email Address *</label>
                      {validationErrors.email && (
                        <div className="invalid-feedback">{validationErrors.email}</div>
                      )}
                    </div>
                  </div>
                </div>

                <h3>Property Contact Information</h3>

                {/* Property Phone and Email - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.property_phone ? 'is-invalid' : ''}`}
                        type="tel"
                        id="propertyPhoneInput"
                        name="property_phone"
                        placeholder="Property Phone"
                        value={formData.property_phone}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="propertyPhoneInput">Property Phone *</label>
                      {validationErrors.property_phone && (
                        <div className="invalid-feedback">{validationErrors.property_phone}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.property_email ? 'is-invalid' : ''}`}
                        type="email"
                        id="propertyEmailInput"
                        name="property_email"
                        placeholder="Property Email"
                        value={formData.property_email}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="propertyEmailInput">Property Email *</label>
                      {validationErrors.property_email && (
                        <div className="invalid-feedback">{validationErrors.property_email}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fax - Full width */}
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.fax ? 'is-invalid' : ''}`}
                    type="text"
                    id="faxInput"
                    name="fax"
                    placeholder="Fax Number (Optional)"
                    value={formData.fax}
                    onChange={handleInputChange}
                    disabled={regStatus !== '2' || isSubmitting}
                  />
                  <label htmlFor="faxInput">Fax Number (Optional)</label>
                  {validationErrors.fax && (
                    <div className="invalid-feedback">{validationErrors.fax}</div>
                  )}
                </div>

                {/* Form Alert - Above Submit Button */}
                {alert.visible && (
                  <div className="mb-3">
                    <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})}>
                      {alert.message}
                    </Alert>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="row mt-4">
                  <div className="col-md-6">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={handleBackToRoomDetails}
                    >
                      Previous
                    </button>
                  </div>
                  <div className="col-md-6 text-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={regStatus !== '2' || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          Next
                          <i className="bi bi-arrow-right ms-2"></i>
                        </>
                      )}
                    </button>
                  </div>
                </div>

              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Contact Information Guidelines
              </CardTitle>
              <Row>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Primary Contact:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>This person will be the main contact for the property</li>
                    <li>Must provide valid phone and email</li>
                    <li>Will receive all property-related communications</li>
                    <li>ID information is optional but recommended</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Property Contact Info:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Optional separate contact details for the property</li>
                    <li>Use if different from primary contact</li>
                    <li>Students may use these details to contact you</li>
                    <li>Fax is optional for administrative purposes</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Final Step:</strong>
                  </div>
                  <div className="small text-muted">
                    This is the final step in property creation. After submission,
                    your property will be reviewed and scheduled for inspection
                    before going live on the platform.
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style jsx>{`
        /* h3 styling to match Step 2 */
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }

        /* MDB5 validation styles - matching Step2Address */
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #fff;
          text-align: left;
          position: relative;
          z-index: 1;
          padding: 0.5rem 0.75rem;
          background: #dc3545;
          border-radius: 0.375rem;
          border: 1px solid #dc3545;
        }
        
        /* For form containers, ensure validation message doesn't overlap */
        .form-outline .invalid-feedback,
        .form-floating .invalid-feedback {
          margin-top: 0.5rem;
          position: relative;
          clear: both;
        }
        
        /* Add arrow pointer to validation message */
        .invalid-feedback::before {
          content: '';
          position: absolute;
          top: -5px;
          left: 1rem;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid #dc3545;
        }

        /* Form floating enhancements */
        .form-floating > .form-control:focus,
        .form-floating > .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .form-floating > label {
          color: #6c757d;
        }

        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label,
        .form-floating > .form-select ~ label {
          opacity: 0.65;
          transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);
        }
      `}</style>
    </div>
  );
};

export default ProviderAddPropertyContact;